import { useCallback, useEffect, useRef, useState } from 'react';

interface UseImageLoadingReturn {
  isLoaded: boolean;
  showPlaceholder: boolean;
  hasError: boolean;
  imgRef: React.RefObject<HTMLImageElement>;
  handleImageLoad: () => void;
  handleImageError: () => void;
}

export const useImageLoading = (priority = false): UseImageLoadingReturn => {
  const [isLoaded, setIsLoaded] = useState(false); // Always start as not loaded
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(priority); // Start loading immediately for priority images
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleImageLoad = useCallback(() => {
    setIsLoaded(true);
    setShowPlaceholder(false);
    setHasError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setIsLoaded(false);
    setShowPlaceholder(false);
    setHasError(true);
  }, []);

  // Intersection Observer to optimize loading (skip for priority images)
  useEffect(() => {
    if (priority || !imgRef.current) return; // Skip intersection observer for priority images

    const getRootMargin = () => {
      const viewportWidth = window.innerWidth;

      if (viewportWidth <= 768) {
        return '300px';
      }

      if (viewportWidth <= 1024) {
        return '250px';
      }

      return '200px';
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: getRootMargin(),
        threshold: 0.1,
      }
    );

    observer.observe(imgRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [priority]);

  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      handleImageLoad();
    }
  }, [handleImageLoad]);

  return {
    isLoaded,
    showPlaceholder: showPlaceholder && shouldLoad,
    hasError,
    imgRef,
    handleImageLoad,
    handleImageError,
  };
};
